import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { shouldWarnRemovingUnionStaff } from "../../../hooks/useShouldWarnRemovingUnionStaff";
import type { WeekData } from "../../../utils/models";
import { getShiftIndexByShiftTimes, shiftTypeToIndex } from "../../../utils/utilFunctions";
import type { SelectedStaff } from "../../AddNurse/AddStaffFooter";
import { findShiftOnDay, type RemoveStaffRow } from "./removeStaffRows";
import { buildRemoveStaffSummary, joinNames, pickLabel, type RemoveStaffPick, removeButtonLabel } from "./removeStaffUtils";

/** A pick's hours and sun / moon: the loaded shift's own bucket, else the listed hours' slot. */
const toPick = (row: RemoveStaffRow, week: WeekData, dayNo: number): RemoveStaffPick | null => {
	const ref = findShiftOnDay(week, dayNo, row.shiftId);
	if (ref) {
		const bucketIndex = shiftTypeToIndex(week.weeklyShifts[ref.shiftIndex].type);
		return { name: row.name, start: ref.shift.startTime, end: ref.shift.endTime, shiftIndex: bucketIndex };
	}
	if (!row.shiftTimes) return null;
	const { start, end } = row.shiftTimes;
	return { name: row.name, start, end, shiftIndex: getShiftIndexByShiftTimes({ startTime: start, endTime: end }) };
};

/**
 * The union members among the picks the department asks to be warned about (all removals, or recurring shifts
 * only) — each named once, however many of her shifts are picked.
 */
const useUnionMemberNames = (rows: readonly RemoveStaffRow[], week: WeekData, dayNo: number): string[] => {
	const { chosenDepartment, getNurseById } = useHospitalData();
	return useMemo(() => {
		const members = rows.filter((row) => {
			const isRecurring = findShiftOnDay(week, dayNo, row.shiftId)?.shift.recurringShiftId != null;
			return shouldWarnRemovingUnionStaff(getNurseById(row.id), chosenDepartment, isRecurring);
		});
		const nameByNurseId = new Map(members.map((row) => [row.id, row.name]));
		return [...nameByNurseId.values()];
	}, [rows, week, dayNo, chosenDepartment, getNurseById]);
};

/**
 * What the footer reads back before the button acts: who (one name, or "3 staff" with each pick and its hours
 * behind it), the date and — for one pick — its hours; the union-member warning naming every union member picked
 * once; and the button's label.
 */
export const useRemoveStaffReadBack = ({
	rows,
	weekIndex,
	dayNo,
	staffingDate,
}: {
	rows: readonly RemoveStaffRow[];
	weekIndex: number;
	dayNo: number;
	staffingDate: DateTime;
}) => {
	const { t } = useTranslation();
	const { hospitalData } = useHospitalData();
	const week = hospitalData.weeks[weekIndex];
	const rowPicks = useMemo(() => rows.map((row) => toPick(row, week, dayNo)), [rows, week, dayNo]);
	const picks = useMemo(() => rowPicks.filter((pick): pick is RemoveStaffPick => pick !== null), [rowPicks]);
	const unionNames = useUnionMemberNames(rows, week, dayNo);
	// Named with their hours, so two shifts of one nurse read apart; a pick with no hours to show is named alone.
	const selectedStaff = useMemo<SelectedStaff[]>(
		() =>
			rows.map((row, index) => {
				const pick = rowPicks[index];
				return { id: row.shiftId, name: pick ? pickLabel(pick.name, pick, t) : row.name };
			}),
		[rows, rowPicks, t],
	);
	return {
		summary: buildRemoveStaffSummary(picks, staffingDate, t),
		selectedStaff,
		unionWarning: unionNames.length > 0 ? t("remove.staff.union.warning", { count: unionNames.length, names: joinNames(unionNames, t) }) : null,
		confirmLabel: removeButtonLabel(rows.length, t),
	};
};
